import type { MovementFilter } from '@simsm/shared-types';
import { apiClient } from '../../app/api-client';

export interface SubmissionPlace {
  registrarIdno: string;
  registrarName: string;
  regionId: string;
  regionName: string;
  localityId: string;
  localityName: string;
  siteId: string;
  siteName: string;
  // Facets only: false for a site the catalogue has inactivated. It is still
  // offered, marked, because its reports are history (UC93-07). Absent on a
  // report row, and absent means active.
  siteActive?: boolean;
}

export interface SubmissionSummary extends SubmissionPlace {
  id: string;
  // The short identifier a person reads and dictates, RPT-2026-000412
  // (specs/031-report-number). `id` stays the key the routes are built from.
  reportNumber: string;
  confirmedAtIso: string;
  source: string;
  rowCount: number;
  operatorIdnp: string;
  // Who reported, in words — recorded at the moment of the confirmation
  // (specs/030-actor-names). Empty on every report confirmed before that change
  // and on every automated one, and the register then falls back to the
  // identifier it has always shown.
  operatorName: string;
}

export interface ListSubmissionsResponse {
  submissions: SubmissionSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface ListSubmissionsQuery {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  registrar?: string;
  region?: string;
  locality?: string;
  sites?: string[];
  /**
   * The report identifier typed into the search box (caiet UC93-02): the number
   * the row prints, or a prefix of the uuid in the report's URL.
   */
  q?: string;
}

export async function listSubmissions(
  query: ListSubmissionsQuery = {},
): Promise<ListSubmissionsResponse> {
  const { data } = await apiClient.get<ListSubmissionsResponse>('/stock/submissions', {
    params: {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 25,
      ...(query.from ? { from: query.from } : {}),
      ...(query.to ? { to: query.to } : {}),
      ...(query.registrar ? { registrar: query.registrar } : {}),
      ...(query.region ? { region: query.region } : {}),
      ...(query.locality ? { locality: query.locality } : {}),
      ...(query.sites && query.sites.length > 0 ? { site: query.sites.join(',') } : {}),
      // Searched server-side, like every other criterion here: the register
      // pages in the database, so a box that filtered the fetched page would
      // search 25 rows out of the whole register.
      ...(query.q ? { q: query.q } : {}),
    },
  });
  return data;
}

// The filter option lists, from the reports this session may see rather than
// from the whole site registry (caiet UC93-03), so a filter never offers a value
// that returns nothing. They depend on the session's scope alone, not on the
// chosen filters, so the query key carries nothing and one fetch serves the page.
export async function listSubmissionFacets(): Promise<SubmissionPlace[]> {
  const { data } = await apiClient.get<{ facets: SubmissionPlace[] }>('/stock/submissions/facets');
  return data.facets;
}

export interface SubmissionDetailRow {
  medicineCode: string;
  // The medicine the code belongs to, resolved by the BFF. Empty when the code
  // is not in the nomenclator any more — the screen then shows the code alone
  // rather than an invented name.
  medicineName: string;
  lot: string;
  lotExpiry: string;
  // 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown.
  lotExpiryPrecision?: string;
  quantitySigned: number;
  reason: string;
  comment: string;
  correctionOf: string;
  // The core.stock row id, what a correction names, and the location it was
  // filed at ('' for none), spec 054 R9.
  id?: string;
  siteId?: string;
}

export interface SubmissionDetail {
  id: string;
  reportNumber: string;
  confirmedAtIso: string;
  source: string;
  operatorIdnp: string;
  operatorName: string;
  registrarId: string;
  registrarName: string;
  siteId: string;
  siteName: string;
  rowCount: number;
  rows: SubmissionDetailRow[];
  // UC50-06 / UC51-03 — the correction window core-service enforces, so the
  // screen states the deadline it will actually be held to (audit E4).
  correctionWindowDays: number;
}

/** `id` is either identifier: the uuid the register links by, or the number. */
export async function getSubmission(id: string): Promise<SubmissionDetail> {
  const { data } = await apiClient.get<SubmissionDetail>(`/stock/submissions/${id}`);
  return data;
}

/**
 * caiet UC93-11 — the movements of one report that match the detail's filters, as the CSV the bff
 * builds, headed in `lang` (docs/shape-ops-report-export.md §3 D5). A `Blob`, so the BOM reaches
 * the disk as sent.
 */
export async function exportSubmissionCsv(
  id: string,
  filter: MovementFilter,
  lang: string,
): Promise<Blob> {
  const search = filter.search.trim();
  const { data } = await apiClient.get<Blob>(
    `/stock/submissions/${encodeURIComponent(id)}/export.csv`,
    {
      params: {
        ...(search ? { q: search } : {}),
        ...(filter.reason ? { reason: filter.reason } : {}),
        lang,
      },
      responseType: 'blob',
    },
  );
  return data;
}
